import React from 'react';
import { Modal, Button, Space, Alert, Input, Typography, List, Card, Tag } from 'antd';
import { ThunderboltOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { SourceScene, ExtractedPanel } from '../../../types';

const { Text } = Typography;

interface ReExtractPanelsModalProps {
  visible: boolean;
  onCancel: () => void;
  scene: SourceScene | null;
  comment: string;
  onCommentChange: (val: string) => void;
  step: 'input' | 'preview';
  onStepChange: (step: 'input' | 'preview') => void;
  extractedPanels: ExtractedPanel[];
  onExtract: () => void;
  onConfirm: () => void;
  loading: boolean;
  getCharacterName: (id: string) => string;
}

const countWords = (text?: string) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).length;
};

const ReExtractPanelsModal: React.FC<ReExtractPanelsModalProps> = ({
  visible,
  onCancel,
  scene,
  comment,
  onCommentChange,
  step,
  onStepChange,
  extractedPanels,
  onExtract,
  onConfirm,
  loading,
  getCharacterName,
}) => {
  return (
    <Modal
      title={`AI Re-draft Panels - Scene: ${scene?.title}`}
      open={visible}
      onCancel={onCancel}
      width={900}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Hủy
        </Button>,
        step === 'input' ? (
          <Button
            key="preview"
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={onExtract}
            loading={loading}
          >
            Gửi yêu cầu cho AI
          </Button>
        ) : (
          <Button
            key="confirm"
            type="primary"
            danger
            icon={<CheckCircleOutlined />}
            onClick={onConfirm}
            loading={loading}
          >
            Xác nhận & Lưu (Sẽ xóa panel cũ)
          </Button>
        ),
        step === 'preview' && (
          <Button key="back" onClick={() => onStepChange('input')}>
            Quay lại nhập comment
          </Button>
        )
      ]}
    >
      {step === 'input' ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            message="Lưu ý"
            description="Hành động này sẽ yêu cầu AI viết lại (Re-draft) dàn ý các panel cho scene này. Sau khi bạn xác nhận lưu, các panel cũ và toàn bộ ảnh/video đã tạo của chúng sẽ bị xóa vĩnh viễn để thay thế bằng dàn ý mới."
            type="warning"
            showIcon
          />
          <div style={{ marginTop: 16 }}>
            <Text strong>Yêu cầu của bạn:</Text>
            <Input.TextArea
              rows={6}
              placeholder="Ví dụ: Hãy tách thành nhiều panel hơn để mô tả kỹ tâm trạng nhân vật, hoặc gộp bớt các panel hội thoại lại..."
              value={comment}
              onChange={(e) => onCommentChange(e.target.value)}
              style={{ marginTop: 8 }}
            />
          </div>
        </Space>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            message="Review dàn ý mới từ AI"
            description="Dưới đây là danh sách panel mới (chưa có mô tả hình ảnh) mà AI gợi ý. Hãy kiểm tra kỹ trước khi bấm 'Xác nhận & Lưu'."
            type="info"
            showIcon
          />
          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <List
              dataSource={extractedPanels}
              renderItem={(panel, index) => (
                <List.Item>
                  <Card size="small" style={{ width: '100%', borderLeft: '4px solid #faad14' }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Space>
                        <Tag color="orange">Gợi ý Panel #{index + 1}</Tag>
                        <Text strong>{panel.title}</Text>
                        <Tag color="default">{countWords(panel.caption)} từ</Tag>
                        {panel.estimatedAudioDuration && (
                          <Tag color="orange">~{panel.estimatedAudioDuration}s audio</Tag>
                        )}
                      </Space>
                      <div style={{ padding: '8px', background: '#fffbe6', borderRadius: 4, border: '1px solid #ffe58f' }}>
                        <Text italic>"{panel.caption}"</Text>
                      </div>
                      {panel.characterIds && panel.characterIds.length > 0 && (
                        <div>
                          <Text type="secondary" style={{ marginRight: 8 }}>Nhân vật:</Text>
                          {panel.characterIds.map(id => (
                            <Tag key={id} color="blue">{getCharacterName(id)}</Tag>
                          ))}
                        </div>
                      )}
                    </Space>
                  </Card>
                </List.Item>
              )}
            />
          </div>
        </Space>
      )}
    </Modal>
  );
};

export default ReExtractPanelsModal;
